import { QueryKey } from '@/configs/queryKeys';
import { Attachment, Translations, UUID } from '@/types/types';
import { ProductTypeCategory } from './ProductTypeCategoryApi';
import { Component } from './ComponentApi';
import { CreatorComponent } from '@/types/creator';
import InvestmentApi, { Investment } from './InvestmentApi';
import ComponentVariationApi from './ComponentVariationApi';
import ProjectTemplateApi from './ProjectTemplateApi';
import EntityApi, { BaseEntity } from './EntityApi';
import APP_CONFIG from '@/configs/appConfig';
import ProjectApi from './ProjectApi';

export enum MontageType {
	INSIDE = 'INSIDE',
	ON_WALL = 'ON_WALL',
	BOTH = 'BOTH',
}

export type RollerBoxHeight =
	(typeof APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_HEIGHTS)[number];
export type RollerBoxDepth =
	(typeof APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_DEPTHS)[number];
export type RollerShaftDiameter =
	(typeof APP_CONFIG.CONFIGURATION_DEFAULTS.ROLLER_BOX.AVAILABLE_SHAFT_DIAMETERS)[number];

// HR - total height
// HW - window height

export interface ShaftDiameterConfig {
	maxHr: number;
	maxHw: number;
}

export type RollerShaftDiameterConfig = Record<
	RollerShaftDiameter,
	ShaftDiameterConfig
>;

export interface ProductTypeRollerBoxHeightConfig {
	rollerBoxHeight: RollerBoxHeight;
	rollerBoxDepth: RollerBoxDepth;
	withMosquitoNet: boolean;
	additionalAttributeValues: UUID[];
	attributeValuesConfig: Record<UUID, RollerShaftDiameterConfig>;
}

export interface ProductType extends BaseEntity {
	name: string;
	description: string;
	image?: Attachment;
	category: ProductTypeCategory;
	components: Component[];
	listOrder: number;
	montageType: MontageType;
	rollerBoxHeightConfigs: ProductTypeRollerBoxHeightConfig[];
	translations: Translations<{
		name: string;
		description: string;
	}>;
}

class ProductTypeApi extends EntityApi<ProductType> {
	constructor() {
		super(QueryKey.productTypes);
		this.getCreatorInitialData = this.getCreatorInitialData.bind(this);
	}

	async getCreatorInitialData({
		investmentId,
		productTypeId,
		projectId,
		projectTemplateId,
	}: {
		investmentId?: UUID;
		productTypeId: UUID;
		projectId?: UUID;
		projectTemplateId?: UUID;
	}) {
		let investment: Investment | undefined;

		if (investmentId)
			investment = await InvestmentApi.mutationGetOne(investmentId);
		const productType = await super.mutationGetOne(productTypeId);

		let projectTemplate;
		if (projectTemplateId)
			projectTemplate =
				await ProjectTemplateApi.mutationGetOne(projectTemplateId);

		const creatorComponents: CreatorComponent[] = [];

		for (const component of productType.components) {
			creatorComponents.push({
				...component,
				variations: await ComponentVariationApi.getVariationsForComponentId(
					component.id
				),
			});
		}

		const allVariations = creatorComponents.flatMap((comp) => comp.variations);

		let project;
		if (projectId) {
			project = await ProjectApi.mutationGetOne(projectId);
			project.componentVariations = project.componentVariations
				.map(
					(componentVariation) =>
						allVariations.find(
							(variation) => variation.id === componentVariation.id
						)!
				)
				.filter(Boolean);
		}

		return {
			investment,
			productType,
			creatorComponents,
			project,
			projectTemplate,
		};
	}
}

export default new ProductTypeApi();
